import React, { useState } from 'react';
import { ChevronDown, Trash2, Loader2, Pin, PinOff, Download, MessageSquarePlus, FolderInput, X } from 'lucide-react';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent,
    DropdownMenuSubTrigger, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

/**
 * Act on the files you picked.
 *
 * Forked rather than shared with tasks' BulkActions: that one acts on whatever
 * the filters currently show ("mark these 4 overdue ones done"), which is a
 * deliberate and different affordance. Files have an explicit selection, so the
 * count here means "the ones with a ticked box" and the label says so.
 *
 * The count is in every destructive label for the same reason as over there —
 * you should never fire one of these without seeing how much it touches.
 */
export default function FileBulkActions({
    count,
    categories,
    onPin,
    onUnpin,
    onSetCategory,
    onDownload,
    onSendToAdvisor,
    onDelete,
    onClear,
}) {
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [busy, setBusy] = useState(false);

    if (!count) return null;

    const run = async (fn) => {
        setBusy(true);
        try {
            await fn();
        } finally {
            setBusy(false);
            setConfirmingDelete(false);
        }
    };

    return (
        <>
            <div className="flex items-center gap-1">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            disabled={busy}
                            data-testid="files-bulk"
                            className="inline-flex items-center gap-1 rounded-lg border border-primary/40 bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary transition-colors hover:bg-primary/25 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : null}
                            {count} selected
                            <ChevronDown className="h-2.5 w-2.5" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                            {count} artifact{count === 1 ? '' : 's'}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />

                        <DropdownMenuItem onSelect={() => run(onPin)} className="gap-2 text-xs">
                            <Pin className="h-3.5 w-3.5" /> Pin {count}
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => run(onUnpin)} className="gap-2 text-xs">
                            <PinOff className="h-3.5 w-3.5" /> Unpin {count}
                        </DropdownMenuItem>

                        <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="gap-2 text-xs">
                                <FolderInput className="h-3.5 w-3.5" /> Move to…
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                                {categories.map((cat) => (
                                    <DropdownMenuItem
                                        key={cat.id}
                                        onSelect={() => run(() => onSetCategory(cat.id))}
                                        className="text-xs"
                                    >
                                        {cat.label}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuSubContent>
                        </DropdownMenuSub>

                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => run(onSendToAdvisor)} className="gap-2 text-xs">
                            <MessageSquarePlus className="h-3.5 w-3.5" /> Send {count} to Advisor
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => run(onDownload)} className="gap-2 text-xs">
                            <Download className="h-3.5 w-3.5" /> Download {count}
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            // Radix closes the menu on select, which would unmount
                            // the dialog with it — so defer opening until the close
                            // settles.
                            onSelect={(e) => {
                                e.preventDefault();
                                setConfirmingDelete(true);
                            }}
                            className="gap-2 text-xs text-red-400 focus:bg-red-500/10 focus:text-red-400"
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Delete {count}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                <button
                    onClick={onClear}
                    disabled={busy}
                    aria-label="Clear selection"
                    title="Clear selection · Esc"
                    className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground disabled:opacity-50"
                >
                    <X className="h-3 w-3" />
                </button>
            </div>

            <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {count} artifact{count === 1 ? '' : 's'}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This deletes every artifact you have selected. It can't be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={busy}
                            onClick={(e) => {
                                e.preventDefault();
                                run(onDelete);
                            }}
                            className="bg-red-500 text-white hover:bg-red-600"
                        >
                            {busy ? 'Deleting…' : `Delete ${count}`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
